/**
 * Unified Device Classification Utility for Browser, CTV, Gaming Consoles, and Vizio TVs.
 */
import { PlatformUtil } from './PlatformUtil';
import { Logger, LoggerLevel } from '../../logger/Logger';
import { Factory } from '../../Factory';

declare const tizen: any;
declare const webapis: any;
declare const webOS: any;

export enum DeviceCapability {
  /**
   * @property HIGH_END for Flagship models.
   */
  HIGH_END = 'HIGH_END',
  /**
   * @property MID_RANGE for Good performance Mid-range models.
   */
  MID_RANGE = 'MID_RANGE',
  /**
   * @property LOW_END for Entry-level models with modest processors and others.
   */
  LOW_END = 'LOW_END',
  /**
   * @property UNKNOWN for devices that cannot be classified.
   */
  UNKNOWN = 'UNKNOWN',
}

// Hardware thresholds
const CORES_HIGH = 8;
const CORES_MEDIUM_MIN = 4;
const RAM_HIGH = 16;
const RAM_MEDIUM_MIN = 8;
const RAM_CTV_HIGH = 3;
const RAM_CTV_MEDIUM_MIN = 1.5;

// Resolution constants
const RESOLUTION_8K = 7680;
const RESOLUTION_4K = 3840;
const RESOLUTION_FHD = 1920;

// Platform version thresholds
const TIZEN_VERSION_MIN = 6.0;
const TIZEN_VERSION_MAX = 8.0;
const WEBOS_VERSION_MIN = 68;
const WEBOS_VERSION_MAX = 94;

// Vizio series classification
const VIZIO_SERIES_HIGH = ['P', 'VQP'];
const VIZIO_SERIES_MEDIUM = ['M', 'V'];
const VIZIO_SERIES_LOW = ['D'];

// Scoring thresholds
const TierThresholds = {
  HIGH_END: 75,
  MID_RANGE: 45,
} as const;

interface DeviceContext {
  resolution: number;
  cores: number;
  ram: number;
  userAgent: string;
  isChrome: boolean;
  isEdge: boolean;
  isBrowser: boolean;
}

interface VizioDeviceInfo {
  subseries?: string;
  size?: number;
  modeResolution?: number;
}

interface WebOSDeviceInfo {
  ddrSize?: string;
  uhd8K?: boolean;
  uhd?: boolean;
  screenWidth?: number;
}

interface ScoreMetrics {
  resolution?: number;
  cores?: number;
  ram?: number;
  version?: number;
}

interface ScoreResult {
  score: number;
  maxScore: number;
}

const TAG_NAME = 'FL_DEVICE_CLASSIFICATION';
const logger: Logger = Factory.createlogger(LoggerLevel.OFF, TAG_NAME);

export class DevicePerformanceClassifier {
  /**
   * Entry point to classify any device based on its platform and context.
   */
  static async getDeviceCapabilities(): Promise<DeviceCapability> {
    // Gaming consoles are always high-end
    if (PlatformUtil.isXbox() || PlatformUtil.isPS5() || PlatformUtil.isPS4()) {
      return DeviceCapability.HIGH_END;
    }

    const util = new DevicePerformanceClassifier();
    const context = util.getDeviceContext();

    // Platform-specific classification
    try {
      if (PlatformUtil.isTizen()) {
        return util.classifyTizen(context);
      }
      if (PlatformUtil.isWebOS()) {
        return await util.classifyWebOS(context);
      }
      if (PlatformUtil.isVizio()) {
        return await util.classifyVizio();
      }
      if (context.isBrowser) {
        return util.classifyBrowser(context);
      }
    } catch (error) {
      logger.warn('Device classification failed:', error);
    }

    return DeviceCapability.UNKNOWN;
  }

  /**
   * Collects browser and platform properties into a structured device context.
   */
  private getDeviceContext(): DeviceContext {
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = PlatformUtil.isChrome();
    const isEdge = PlatformUtil.isEdge();

    return {
      resolution: this.getScreenResolution(),
      cores: navigator.hardwareConcurrency ?? 0,
      ram: (navigator as any).deviceMemory ?? 0,
      userAgent,
      isChrome,
      isEdge,
      isBrowser: isChrome || isEdge || /safari/.test(userAgent) || /firefox/.test(userAgent),
    };
  }

  /**
   * Safely gets screen resolution
   */
  private getScreenResolution(): number {
    if (typeof window === 'undefined') return 0;

    const screen = window.screen;
    const pixelRatio = window.devicePixelRatio || 1;

    return (screen?.width || 0) * pixelRatio;
  }

  /**
   * Classifies a browser-based environment (Chrome, Edge, etc.) into a device tier.
   */
  private classifyBrowser(context: DeviceContext): DeviceCapability {
    const { resolution, cores, ram } = context;
    const useRam = PlatformUtil.isChrome() || PlatformUtil.isEdge();

    const { score, maxScore } = this.computeScores({
      resolution,
      cores,
      ram: useRam ? ram : undefined,
    });

    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    const tier = this.classifyTier(percentage);

    logger.log('Browser classification:', {
      tier,
      context: {
        resolution,
        cores,
        ram: useRam ? ram : 'N/A',
        browser: context.isChrome ? 'Chrome' : context.isEdge ? 'Edge' : 'Other',
      },
      percentage: Math.round(percentage * 100) / 100,
      score,
      maxScore,
    });

    return tier;
  }

  /**
   * Classifies a Tizen-based smart TV into a device tier.
   */
  private classifyTizen(context: DeviceContext): DeviceCapability {
    const tizenVersion = this.getTizenVersionFromUserAgent(context.userAgent);
    const resolutionVal = this.getTizenResolution();
    const ramInGB = this.getTizenMemory();
    const { cores } = context;

    const { score, maxScore } = this.computeScores(
      { resolution: resolutionVal, cores, ram: ramInGB, version: tizenVersion },
      this.getTizenVersionScore.bind(this),
      true,
    );

    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    const tier = this.classifyTier(percentage);

    logger.log('Tizen classification:', {
      tier,
      tizenVersion,
      resolution: resolutionVal,
      ramInGB,
      cores,
      score,
      maxScore,
      percentage: Math.round(percentage * 100) / 100,
    });

    return tier;
  }

  /**
   * Classifies a WebOS-based smart TV into a device tier.
   */
  private async classifyWebOS(context: DeviceContext): Promise<DeviceCapability> {
    if (!webOS?.deviceInfo) {
      logger.warn('WebOS deviceInfo API not available');
      return DeviceCapability.UNKNOWN;
    }

    try {
      const device = await this.getWebOSDeviceInfo();
      const chromiumVersion = this.getWebOSVersionFromUserAgent(context.userAgent);
      const cores = context.cores || 1;
      const ram = this.getWebOSMemory(device);
      const resolution = this.getWebOSResolution(device);

      const { score, maxScore } = this.computeScores(
        { resolution, cores, ram, version: chromiumVersion },
        this.getWebOSVersionScore.bind(this),
        true,
      );

      const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
      const tier = this.classifyTier(percentage);

      logger.log('WebOS classification:', {
        tier,
        resolution,
        cores,
        ram,
        chromiumVersion,
        score,
        maxScore,
        percentage: Math.round(percentage * 100) / 100,
      });

      return tier;
    } catch (error) {
      logger.warn('WebOS classification failed:', error);
      return DeviceCapability.UNKNOWN;
    }
  }

  /**
   * Gets WebOS device info
   */
  private async getWebOSDeviceInfo(): Promise<WebOSDeviceInfo> {
    return new Promise<WebOSDeviceInfo>((resolve, reject) => {
      try {
        webOS.deviceInfo((device: WebOSDeviceInfo) => {
          resolve(device || {});
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Classifies a VIZIO smart TV into a device tier.
   */
  private async classifyVizio(): Promise<DeviceCapability> {
    try {
      const [info, hdr] = await Promise.all([this.getVizioDeviceInfo(), this.getVizioHDRCapabilities()]);

      const series = info.subseries || '';
      const size = info.size || 0;
      const resolution = info.modeResolution || -1;
      const hdrValue = hdr.map(cap => cap.toLowerCase());

      const scores = this.computeVizioScore(series, size, resolution, hdrValue);
      const percentage = scores.maxScore > 0 ? (scores.score / scores.maxScore) * 100 : 0;
      const tier = this.classifyTier(percentage);

      logger.log('Vizio classification:', {
        tier,
        series,
        size,
        hdr: hdrValue,
        resolution,
        totalScore: scores.score,
        maxScore: scores.maxScore,
        percentage: Math.round(percentage * 100) / 100,
      });

      return tier;
    } catch (error) {
      logger.warn('Vizio classification failed:', error);
      return DeviceCapability.UNKNOWN;
    }
  }

  /**
   * Classifies device tier based on percentage score
   */
  private classifyTier(percentage: number): DeviceCapability {
    if (percentage >= TierThresholds.HIGH_END) return DeviceCapability.HIGH_END;
    if (percentage >= TierThresholds.MID_RANGE) return DeviceCapability.MID_RANGE;
    return DeviceCapability.LOW_END;
  }

  // --- Helper Methods ---
  private getResolutionScore(resolution: number): number {
    if (resolution >= RESOLUTION_8K) return 3;
    if (resolution >= RESOLUTION_4K) return 2;
    if (resolution >= RESOLUTION_FHD) return 1;
    return 0;
  }

  private getCpuScore(cores: number): number {
    if (cores >= CORES_HIGH) return 3;
    if (cores >= CORES_MEDIUM_MIN) return 2;
    return 1;
  }

  private getRamScore(ramGB: number, isTizenOrWebOS = false): number {
    if (isTizenOrWebOS) {
      if (ramGB >= RAM_CTV_HIGH) return 3;
      if (ramGB >= RAM_CTV_MEDIUM_MIN) return 2;
    } else {
      if (ramGB >= RAM_HIGH) return 3;
      if (ramGB >= RAM_MEDIUM_MIN) return 2;
    }
    return 1;
  }

  private getTizenVersionFromUserAgent(ua: string): number | undefined {
    const regex = /tizen[\s\/:]+(\d+(?:\.\d+)?)/i;
    const match = regex.exec(ua);
    return match ? parseFloat(match[1]) : undefined;
  }

  private getTizenMemory(): number | undefined {
    try {
      if (typeof tizen !== 'undefined' && tizen.systeminfo) {
        return tizen.systeminfo.getTotalMemory() / 1024 ** 3;
      }
    } catch (error) {
      logger.warn('Tizen RAM fetch error:', error);
    }
    return (navigator as any).deviceMemory || undefined;
  }

  private getTizenResolution(): number {
    const isUHD = webapis?.productinfo?.isUdPanelSupported?.();
    const is8K = webapis?.productinfo?.is8KPanelSupported?.();
    return is8K ? RESOLUTION_8K : isUHD ? RESOLUTION_4K : RESOLUTION_FHD;
  }

  private getWebOSVersionFromUserAgent(ua: string): number | undefined {
    const match = /chrome\/(\d+)/i.exec(ua);
    return match ? parseInt(match[1], 10) : undefined;
  }

  private getWebOSMemory(device: WebOSDeviceInfo): number | undefined {
    if (!device.ddrSize) return undefined;

    const memoryStr = device.ddrSize.replace(/G/i, '');
    const memory = parseFloat(memoryStr);

    return isNaN(memory) ? undefined : memory;
  }

  private getWebOSResolution(device: WebOSDeviceInfo): number {
    const { uhd8K, uhd, screenWidth } = device;

    if (uhd8K) return RESOLUTION_8K;
    if (uhd || (screenWidth && screenWidth >= RESOLUTION_4K)) return RESOLUTION_4K;
    return RESOLUTION_FHD;
  }

  private getTizenVersionScore(version?: number): number {
    if (version === undefined || isNaN(version)) return 0;
    if (version >= TIZEN_VERSION_MAX) return 3;
    if (version >= TIZEN_VERSION_MIN) return 2;
    return 1;
  }

  private getWebOSVersionScore(chromiumVersion?: number): number {
    if (!chromiumVersion || isNaN(chromiumVersion)) return 0;
    if (chromiumVersion >= WEBOS_VERSION_MAX) return 3;
    if (chromiumVersion >= WEBOS_VERSION_MIN) return 2;
    return 1;
  }

  private computeScores(metrics: ScoreMetrics, versionScoreFn?: (v: number) => number, isCTV = false): ScoreResult {
    let score = 0;
    let maxScore = 0;

    // Resolution scoring
    if (typeof metrics.resolution === 'number' && !isNaN(metrics.resolution)) {
      score += this.getResolutionScore(metrics.resolution);
      maxScore += 3;
    }

    // CPU cores scoring
    if (typeof metrics.cores === 'number' && !isNaN(metrics.cores)) {
      score += this.getCpuScore(metrics.cores);
      maxScore += 3;
    }

    // RAM scoring
    if (typeof metrics.ram === 'number' && !isNaN(metrics.ram)) {
      score += this.getRamScore(metrics.ram, isCTV);
      maxScore += 3;
    }

    // Version scoring (platform-specific)
    if (typeof metrics.version === 'number' && !isNaN(metrics.version) && versionScoreFn) {
      score += versionScoreFn(metrics.version);
      maxScore += 3;
    }

    return { score, maxScore };
  }

  private computeVizioScore(series: string, size: number, resolution: number, hdrCapabilities: string[]): ScoreResult {
    let score = 0;
    let maxScore = 0;

    // Series scoring
    if (series && typeof series === 'string') {
      score += this.calculateSeriesScore(series);
      maxScore += 3;
    }

    // Size scoring
    if (typeof size === 'number' && !isNaN(size) && size > 0) {
      score += this.calculateSizeScore(size);
      maxScore += 3;
    }

    // Resolution scoring
    if (typeof resolution === 'number' && !isNaN(resolution)) {
      score += this.calculateVizioResolutionScore(resolution);
      maxScore += 3;
    }

    // HDR capabilities scoring
    if (Array.isArray(hdrCapabilities) && hdrCapabilities.length > 0) {
      score += this.calculateHDRScore(hdrCapabilities);
      maxScore += 3;
    }

    return {
      score,
      maxScore,
    };
  }

  /**
   * Gets Vizio device information with proper error handling
   */
  private async getVizioDeviceInfo(): Promise<VizioDeviceInfo> {
    return new Promise<VizioDeviceInfo>((resolve, reject) => {
      try {
        const vizioApi = (window as any).VIZIO;
        if (!vizioApi?.getDeviceInformation) {
          reject(new Error('VIZIO getDeviceInformation API not available'));
          return;
        }

        const result = vizioApi.getDeviceInformation();

        if (result && typeof result.then === 'function') {
          result.then(resolve).catch(reject);
        } else {
          const deviceInfo: VizioDeviceInfo = {
            subseries: result.SYSTEM_INFO?.SUBSERIES,
            size: result.SYSTEM_INFO?.SIZE,
            modeResolution: result.SYSTEM_INFO?.MODEL_RESOLUTION,
          };
          resolve(deviceInfo || {});
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Gets Vizio HDR capabilities
   */
  private async getVizioHDRCapabilities(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      try {
        const vizioApi = (window as any).VIZIO;
        if (!vizioApi?.getDeviceHDRCapabilities) {
          reject(new Error('VIZIO getDeviceHDRCapabilities API not available'));
          return;
        }

        vizioApi.getDeviceHDRCapabilities((capabilities: string[]) => {
          resolve(capabilities || []);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Calculates score based on Vizio series
   */
  private calculateSeriesScore(series: string): number {
    const upperSeries = series.toUpperCase();

    if (VIZIO_SERIES_HIGH.some(s => upperSeries.includes(s))) return 3;
    if (VIZIO_SERIES_MEDIUM.some(s => upperSeries.includes(s))) return 2;
    if (VIZIO_SERIES_LOW.some(s => upperSeries.includes(s))) return 1;

    return 0; // Unknown series
  }

  /**
   * Calculates score based on screen size
   */
  private calculateSizeScore(size: number): number {
    if (size >= 55) return 3;
    if (size >= 43) return 2;
    if (size > 0) return 1;
    return 0;
  }

  /**
   * Calculates score based on Vizio resolution index
   */
  private calculateVizioResolutionScore(resolution: number): number {
    // Vizio RESOLUTION ARRAY = [UHD, FHD, HD, SD] (0-based index)
    switch (resolution) {
      case 0:
        return 3; // UHD/4K
      case 1:
        return 2; // FHD
      case 2:
        return 1; // HD
      case 3:
        return 1; // SD
      default:
        return 0; // Unknown
    }
  }

  /**
   * Calculates score based on HDR capabilities
   */
  private calculateHDRScore(hdrCapabilities: string[]): number {
    if (hdrCapabilities.some(cap => cap.includes('hdr10+') || cap.includes('dolby'))) {
      return 3;
    }
    if (hdrCapabilities.some(cap => cap.includes('hdr10'))) {
      return 2;
    }
    if (hdrCapabilities.some(cap => cap.includes('sdr'))) {
      return 1;
    }
    return 0;
  }
}
